import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '../utils/logger.js';

const DEFAULT_LOG_MODULE = 'useApiRequest';

const normalizeError = (error) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack
    };
  }
  return {
    value: String(error)
  };
};

const useApiRequest = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const execute = useCallback(async (asyncFn, options = {}) => {
    const { onSuccess, onError, logModule = DEFAULT_LOG_MODULE } = options;

    if (typeof asyncFn !== 'function') {
      const requestError = new TypeError('execute expects an async function');
      if (isMountedRef.current) {
        setError(requestError);
        setLoading(false);
      }
      logger.error(logModule, 'Request failed', {
        source: { file: 'shared/hooks/useApiRequest.js', method: 'execute' },
        error: normalizeError(requestError)
      });
      if (typeof onError === 'function') {
        onError(requestError);
      }
      throw requestError;
    }

    if (isMountedRef.current) {
      setLoading(true);
      setError(null);
    }
    logger.debug(logModule, 'Request started', {
      source: { file: 'shared/hooks/useApiRequest.js', method: 'execute' }
    });

    try {
      const responseData = await asyncFn();
      if (isMountedRef.current) {
        setData(responseData);
        setError(null);
      }
      logger.info(logModule, 'Request succeeded', {
        source: { file: 'shared/hooks/useApiRequest.js', method: 'execute' }
      });
      if (typeof onSuccess === 'function') {
        onSuccess(responseData);
      }
      return responseData;
    } catch (requestError) {
      if (isMountedRef.current) {
        setError(requestError);
      }
      logger.error(logModule, 'Request failed', {
        source: { file: 'shared/hooks/useApiRequest.js', method: 'execute' },
        error: normalizeError(requestError)
      });
      if (typeof onError === 'function') {
        onError(requestError);
      }
      throw requestError;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const reset = useCallback(() => {
    if (isMountedRef.current) {
      setData(null);
      setError(null);
      setLoading(false);
    }
  }, []);

  return {
    data,
    loading,
    error,
    execute,
    reset
  };
};

export default useApiRequest;
